/**
 * Document counts of one document-source connection as a compact chip row:
 * total on the source, indexed, pending, failed. Polled while visible so a
 * running sync is reflected without a reload.
 */

import React from 'react';
import { Box, Chip, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useConnectionStats } from '../../../hooks/api/useDocumentSources';

/** Interval for the counters while the card is visible. */
const STATS_REFRESH_MS = 30_000;

interface ConnectionStatsChipsProps {
  connectionId: string;
  /** Whether the connection ever synced; without a sync the counts are meaningless. */
  hasSynced: boolean;
}

export const ConnectionStatsChips: React.FC<ConnectionStatsChipsProps> = ({ connectionId, hasSynced }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useConnectionStats(connectionId, {
    queryOptions: { refetchInterval: STATS_REFRESH_MS, refetchIntervalInBackground: false },
  });

  if (!hasSynced) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {t('documentSources.stats.noSync')}
      </Typography>
    );
  }
  if (isLoading && !data) {
    return <Skeleton variant="rounded" height={24} sx={{ mt: 1 }} />;
  }
  if (!data) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }} aria-label={t('documentSources.stats.title')}>
      <Chip size="small" variant="outlined" label={t('documentSources.stats.total', { count: data.total_files })} />
      <Chip size="small" color="success" variant="outlined" label={t('documentSources.stats.indexed', { count: data.processed_files })} />
      {data.pending_files > 0 && (
        <Chip size="small" color="warning" variant="outlined" label={t('documentSources.stats.pending', { count: data.pending_files })} />
      )}
      {data.failed_files > 0 && (
        <Chip size="small" color="error" variant="outlined" label={t('documentSources.stats.failed', { count: data.failed_files })} />
      )}
    </Box>
  );
};
